// contexts/treatmentPlanning/index.js

import { defineAsyncComponent } from 'vue';

/**
 * Published interface of the Treatment Planning context. Everything outside imports from here and
 * nothing deeper — the layers behind it are internal.
 *
 * This context opened with two slices: the curriculum one - the status vocabularies, the rules that
 * keep a target's dates honest, and the one writer of the target-count projection - and the service
 * delivery value object the session plan needed. The Treatment Plan aggregate it is named after does
 * not exist in code yet. See the README for what it owns today.
 *
 * @context What a clinician intends to teach: programs, targets, and the status rules governing them
 */

// The target status vocabulary. Published because `boot/globals` now resolves its labels from here
// rather than holding a second copy, and because the importer validates against it. Three parallel
// copies of these seven values is what this replaces.
export {
  isTargetStatus,
  TARGET_STATUS,
  TARGET_STATUS_META,
} from './domain/constants/targetStatus';

// What a file may set on a program or a target, declared in this context's own terms. Onboarding
// translates it; see its ImportTarget port for why the declaration is authored here and not there.
export {
  MAINTENANCE_INTERVAL_DAYS,
  PROGRAM_IMPORTABLE_FIELDS,
} from './domain/constants/importableFields';

export {
  PROGRAM_STATUS,
  PROGRAM_STATUS_META,
} from './domain/constants/programStatus';

// Whether a data type may hold sub-targets. Published because onboarding refuses an import that
// gives steps to a type that cannot hold them, and the answer is this context's to give.
export {
  canHaveSubTargets,
} from './domain/constants/targetDataType';

// A target's status and the dates recording how it got there, as one value object. `withStatus`
// clears what a new status contradicts and stamps nothing, and `dateConflicts` judges a lifecycle
// that already exists - which is the question an import asks. The operations that stamp a real
// timestamp stay internal until something outside this context has one.
//
// `LIFECYCLE_DATES` names the four as a set, for a caller that has to reason about them without
// knowing which status governs which. The importer needs exactly that: when a file states a date
// but no status, there is no status to reconcile the date against, and the whole set is what it
// has to leave alone. Unless the dates imply one: `statusImpliedByDates` is how it asks.
export {
  createTargetLifecycle,
  dateConflicts,
  LIFECYCLE_DATES,
  statusImpliedByDates,
  withStatus,
} from './domain/model/TargetLifecycle';

export {
  createTarget,
} from './domain/model/Target';

// How a data type is measured, and what a program should say about the targets arriving with it.
// Published because an import has the targets before the program exists and has to decide, and
// because `boot/globals` is closed to a context — see `measurementMetric` for why that matters.
export {
  MEASUREMENT_METRIC,
} from './domain/constants/measurementMetric';

export {
  deriveProgramDefaults,
} from './domain/services/programDefaults';

// The two rules a data type places on the target carrying it and the program holding it. Published
// because only an import has to judge a file against them: the UI enforces both by never offering
// the invalid combination, so nothing else ever had to ask.
export {
  dataTypeHasField,
  isDataTypeAllowedInProgramType,
  isMetricAllowedForDataType,
  measuresMetric,
  TARGET_DATA_TYPE,
} from './domain/constants/targetDataType';

// What a workflow evaluates. Published because an import names a target's workflow and metric
// separately, and a pair that disagrees leaves the workflow invisible on the target.
export {
  workflowTypeMetric,
} from './domain/constants/workflowType';

// The target-count projection, and its reader. Published together because a caller that computes
// the count without using the reader is how two screens come to disagree about one program.
export {
  countTargetsPerStatus,
} from './domain/services/targetProgress';

// The persistence mapper, published only while legacy callers still hold stored documents and this
// context owns no read path of its own. It stops being part of the interface once the write path
// moves in behind a repository, which is what a later pass does.
export {
  toTarget,
} from './infrastructure/mappers/targetMapper';

// Service delivery — what is delivered, and where, for a single encounter.
//
// The FACTORY takes treatment service ids and the READER returns them, while the value object owns
// the `{ treatmentServiceId }` entry wrapper the document stores. That asymmetry is the point:
// nothing outside this context builds an entry, so an entry growing a second field is a change here
// rather than a change everywhere.
//
// `isSameServiceDelivery` is published because the pair is replaced as a WHOLE. When a delivery
// arrives from more than one source, a caller needs to ask whether two are the same value — never
// to merge them field by field, which produces a pair neither source authored.
export {
  createServiceDelivery,
  EMPTY_SERVICE_DELIVERY,
  isEmptyServiceDelivery,
  isSameServiceDelivery,
  placeOfServiceIdOf,
  treatmentServiceIdsOf,
  withPlaceOfService,
  withTreatmentServices,
} from './domain/model/ServiceDelivery';

// The mapper is published because this context owns no write path yet: the session plan and the
// clinical activity are both still saved by legacy code holding the raw document, so that code needs
// the translation. It stops being part of the interface once a repository lives in here.
//
// ABSENT AND EMPTY ARE DIFFERENT ON BOTH SIDES. `null` is a record with no service delivery; an
// empty delivery is one where nothing has been chosen. The activity editor decides whether a save
// clears a stored value on exactly that distinction.
//
// `canCarryServiceDelivery` is part of the same translation: it answers which stored activity types
// may hold a delivery, and `sessionActivities` conflates staff time, clinical delivery and
// administrative artifacts under one `type`. That question is asked in the mud's terms, so it is
// answered in the layer allowed to name them.
export {
  canCarryServiceDelivery,
  toDeclaredServiceDelivery,
  toServiceDelivery,
  toServiceDeliveryDocument,
} from './infrastructure/mappers/serviceDeliveryMapper';

// The two fields as one control, because they are one value. A caller renders this rather than two
// selects of its own, which is what stops a screen assembling half a delivery. The catalogs it
// offers are required props: this context does not own that data, and reading the store for it
// would make the barrel unusable by `fb/` and `boot/`, which import the mappers above.
//
// DEFERRED so that importing a status vocabulary does not drag a component and the design system in
// with it. `boot/globals` enters this barrel at module scope during startup and wants two constants.
export const ServiceDeliveryFields = defineAsyncComponent(
  () => import('./ui/components/ServiceDeliveryFields.vue'),
);
